/**
 * FirmaStandListPage.jsx — Standlarım (Dekoratör görünümü)
 * Route: /Firma/Standlar
 *
 * FirmaAnasayfaListPage'deki "İnşa İzin Durumu" / "Proje Evrakları Durumu"
 * panellerinin "Tümünü Gör" bağlantılarından açılan tam müşteri-stand listesi.
 * Aynı veri kaynağını kullanır — standOverviewService.getStandOverviewList,
 * decoratorFirmaId = bu firma olacak şekilde süzülmüş. Salt okunur: oluşturma/
 * silme yok, sadece detaya (FirmaStandDetailPage) gidiş.
 *
 * 2026-07-24 revizyonu (bkz. `/fromImageToLayout`): Tablo yerine, bu klasördeki
 * kardeş sayfalarla (FirmaProjelerListPage.jsx, FirmaTaleplerListPage.jsx,
 * Management/Projects/StandListPage.jsx) aynı görsel dili paylaşan kart satırı
 * — koyu stand rozeti (StandThumbnail), gri bilgi rozetleri (StandInfoGrid:
 * Paket/Kat/Alan/Yükseklik) ve ikon-kare durum satırı (StandStatusTiles).
 * Referans görseldeki ilk 2 kutu İnşa İzni + Borç Durumu, sonraki 4'ü Teknik
 * Talepler (Elektrik/Su/Hava/İnternet) — bu dört kalem standTalepService.js'te
 * standId=mongo .id ile tutulur, roster'daki hiçbir yerde toplu döndürülmediği
 * için (bkz. firmaTalepListService.js'teki aynı çözüm, buildStandSummary)
 * standlar yüklendikten sonra ayrı bir Promise.all effect'iyle çekilip
 * teknikByStandId'ye eklenir.
 *
 * 2026-08-29 revizyonu (bkz. docs/guides/missing-pills-guide.md): gri bilgi
 * kutularının (StandInfoGrid) alanları artık mock-only _standListDesignOverrides
 * haritasından DEĞİL, GET /standTalep/list satırının kendisinden geliyor — canlı
 * fuarlarda kutuların tamamı boş kalıyordu, çünkü hepsi koşullu render. İnşa İzni de
 * requiredCount/approvedCount yaklaşımını bıraktı: backend'in dört durumlu
 * constructionPermit'i kullanılıyor ("Gerek yok" ile "Verilmedi" ikisi de granted:false
 * ama zıt anlamlı). Köprü (getProjectByStandId) artık liste render'ını beklet-MİYOR.
 *
 * 2026-07-31 revizyonu: stand rozetinin (StandThumbnail) arka planı artık sabit
 * koyu gri değil — "Stand Durumu" sekmesindeki (FirmaStandOverviewPage.jsx,
 * standDurumuService.getStandDurumuOzet) aynı `seviye` rengini taşır
 * (standDurumuService.SEVIYE_RENK, üç sayfa arasında ortak). Evrak durumu
 * dekoratör firması bazlı olduğundan (stand bazlı değil) firmaId için bir kez
 * çekilir, sonra stands yüklendikten sonra ayrı bir Promise.all effect'iyle her
 * stand için özet hesaplanıp standDurumuByStandId'ye eklenir — teknikByStandId
 * ile aynı iki aşamalı desen.
 *
 * 2026-07-31 revizyonu (ikinci, `/revise-page`): standCode ("4J44" gibi,
 * organizatörün atadığı salon/stant kodu — rozetin kendisinde gösterilir) ile
 * "Stand Tabela Adı" (standın üzerindeki tabelada yazan, katılımcının seçtiği
 * serbest metin — standOverviewService'teki yeni `tabelaAdi` alanı, standCode'dan
 * TAMAMEN AYRI) daha önce yanlışlıkla karıştırılıyordu. Düzeltme: rozetin
 * (StandThumbnail) YANINDAKİ eski `firmaShortName`+`salonName` sütunu artık üç
 * satır gösteriyor — Stand Tabela Adı (kalın, öncesinde firmaShortName'in
 * kapladığı görsel ağırlıkta), Katılımcı Firma Adı (tam unvan, `firmaName` —
 * kısaltma `firmaShortName` artık bu kartta gösterilmiyor), Dekoratör Adı
 * (`dekoratorFirmaName`, öncesinde bu kartta hiç yoktu). Ayrı `salonName` metni
 * kaldırıldı — rozet zaten salon/kod bilgisini taşıyor, tekrar oluyordu.
 */
import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container, Card, CardBody, Row, Col, Input, Button, Spinner, Alert,
  Pagination, PaginationItem, PaginationLink,
} from "reactstrap";
import {
  Eye, RefreshCw, Star,
  HardHat, Coins,
} from "lucide-react";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import MainContext from "../../Components/Contexts/MainContext";
import { TEKNIK_ICONS } from "../../Components/Projects/StandPills";
import StandRolRozeti from "../../Components/Firma/StandRolRozeti";
import StandInfoGrid from "../../Components/Firma/StandInfoGrid";
import StandKodKutusu from "../../Components/Firma/StandKodKutusu";
import {
  getStandOverviewList, CONSTRUCTION_PERMIT_GRANTED_STATUS_IDS,
} from "../../services/standOverviewService";
import { getProjectByStandId } from "../../services/projectService";
// WAS (until #403): import { MOCK_DECORATOR_FIRMA_ID, TEST_DECORATOR_FIRMA_ID, getFirmaProfile, getFirmaDocuments, getFirmaBelgeleri } from "../../services/firmaProfileService";
import { MOCK_DECORATOR_FIRMA_ID, getFirmaProfile, getFirmaDocuments, getFirmaBelgeleri } from "../../services/firmaProfileService";
// #403: the live branch takes the logged-in company's id from the portal context (GET /portal/context).
import { usePortal } from "../../Components/Contexts/PortalContext";
import { getTeknikTalepler, TEKNIK_TALEP_KATEGORILERI } from "../../services/standTalepService";
import { getStandDurumuOzet, SEVIYE_RENK } from "../../services/standDurumuService";
import { useTranslation } from "react-i18next";
import i18n from "../../i18n";

function getCurrentFirmaId() {
  try {
    const user = JSON.parse(localStorage.getItem("user"));
    return user?.firmaId ?? MOCK_DECORATOR_FIRMA_ID;
  } catch {
    return MOCK_DECORATOR_FIRMA_ID;
  }
}

// Canlı backend'e sahip test fuarları (298/299) — bkz. FirmaKartPage.jsx/
// FirmaTaleplerListPage.jsx'teki aynı desen (dosya-yerel kopya, ortak util yok).
// Bu fuarlarda firmaId gerçek dekoratöre (TEST_DECORATOR_FIRMA_ID = 49858)
// sabitlenir — login henüz kendi firmaId'sini çözemiyor (bkz. o sayfaların bilinen
// kısıtlaması).
// WAS (until #398): only the two seeded test fairs ran on the real backend, every other fair fell back to mock data:
// const LIVE_TEST_FUAR_IDS = [298, 299];
// const isLiveTestFuarId = (fuarId) => LIVE_TEST_FUAR_IDS.includes(Number(fuarId));
// #398: a full-simulation test creates a NEW fair, so every real fair id is live.
const isLiveTestFuarId = (fuarId) => Number(fuarId) > 0;

// 2026-08-29: taahhütnameleri stand_file 5'in alt dosyalarından TÜRETEN yerel
// yardımcı kaldırıldı — bu sayfa commitments[]'i zaten hiç render etmiyordu
// (kutucuklar doğrudan detay sayfasına yönlendiriyor), ve roster artık doğru
// şekli döndürüyor. Bkz. missing-pills-guide.md §5.

// Canlı standId köprüsü. 2026-08-29'dan beri liste render'ı için GEREKLİ DEĞİL —
// bilgi kutuları ve İnşa İzni roster'dan geliyor (bkz. missing-pills-guide.md).
// Köprüden geriye sadece Proje Beyanı kapısı (projeBeyani.declared) ile belge
// sayaçları kaldı, bu yüzden ikinci ve ENGELLEMEYEN bir aşamada çekiliyor.
function mergeLiveProjectIntoStand(stand, bridged) {
  const requiredCount = bridged?.requiredCount ?? 0;
  const approvedCount = bridged?.approvedCount ?? 0;
  return {
    ...stand,
    requiredCount,
    approvedCount,
    // ⚠️ 2026-08-29: constructionPermitGranted/commitments ARTIK BURADA EZİLMİYOR —
    // roster'ın constructionPermit/commitments alanları taşıyor.
    // "Proje Beyanı" yapılmış mı — sadece bir proje kaydının VAR OLMASI değil,
    // dekoratörün gerçekten beyan etmiş olması (bkz. createStandProject'in
    // forceLive dalı — staff bir proje kabuğu oluşturmuş ama henüz beyan
    // edilmemiş olabilir, bu durumda hâlâ "projeYok" sayılır).
    projectDeclared: bridged?.projeBeyani?.declared === true,
  };
}

// Renkler sabit hex — bu temada çalışma zamanı --bs-*/--vz-* CSS değişkenleri
// yok (bkz. aynı çözüm FirmaProjelerListPage.jsx/FirmaTaleplerListPage.jsx'teki).
const NEUTRAL_GRAY = "#878a99";
const STATUS_HEX = { success: "#0ab39c", danger: "#f06548" };

function VDivider() {
  return (
    <div
      className="d-none d-md-block"
      style={{ width: 1, alignSelf: "stretch", backgroundColor: "currentColor", opacity: 0.15 }}
    />
  );
}

// #487: the stand-code box (StandThumbnail) moved to Components/Firma/StandKodKutusu.jsx, shared
// with the Dekoratör Projeler list and the Anasayfa widgets. Its colour is still this page's: the
// Stand Durumu sekmesindeki (standDurumuService.js `seviye`, bkz. FirmaStandOverviewPage.jsx'teki
// GRAND_DURUMU_META) renk, bkz. dosya sonundaki standDurumuByStandId effect'i; veri henüz
// yüklenmediyse SEVIYE_RENK.projeYok (nötr gri).

// #488: the grey info pills (InfoBox / StandInfoGrid) moved to Components/Firma/StandInfoGrid.jsx,
// shared with the Dekoratör Projeler list — this page used to draw only four of its seven.

// Durum karesi — İnşa İzni/Borç Durumu (boolean, "iyi" yeşil / "kötü" kırmızı)
// ve Teknik Talepler (talep edilmişse kategori rengi, edilmemişse gri) için ortak
// render. Tıklanınca ilgili detay sekmesine yönlendirir (aynı desen
// FirmaTaleplerListPage.jsx'teki TalepTile'da).
function StatusTile({ icon: Icon, color, opacity = 1, label, onClick }) {
  return (
    <div
      role="button"
      tabIndex={0}
      className="d-flex align-items-center justify-content-center rounded-3 flex-shrink-0"
      style={{ width: 40, height: 40, background: color, opacity, cursor: "pointer" }}
      title={label}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick();
        }
      }}
    >
      <Icon size={18} color="#fff" />
    </div>
  );
}

function StandStatusTiles({ s, teknik, navigate }) {
  const { t } = useTranslation("portal");
  // İnşa İzni: canlıda backend'in dört durumlu cevabı (1 "Gerek yok" / 4 "Verildi" /
  // 7 "Nehir İSGM uygun gördü" = izin var), mock'ta eski belge sayımı. Artık
  // requiredCount/approvedCount'tan TÜRETİLMİYOR — bkz. missing-pills-guide.md §5.
  const izinVar = s.constructionPermit
    ? CONSTRUCTION_PERMIT_GRANTED_STATUS_IDS.includes(s.constructionPermit.statusId)
    : (s.constructionPermitGranted ?? (s.requiredCount > 0 && s.approvedCount === s.requiredCount));
  const hasDebt = !!s.hasDebt;
  const teknikByKey = {};
  (teknik || []).forEach((r) => { teknikByKey[r.categoryKey] = r; });

  return (
    <div className="d-flex align-items-center flex-wrap gap-2">
      <StatusTile
        icon={HardHat}
        color={izinVar ? STATUS_HEX.success : STATUS_HEX.danger}
        label={t("standlarim.insaIzni", { v: izinVar ? t("standlarim.verildi") : t("standlarim.verilmedi") })}
        onClick={() => navigate(`/Firma/Standlar/${s.id}?tab=commitments`)}
      />
      <StatusTile
        icon={Coins}
        color={hasDebt ? STATUS_HEX.danger : STATUS_HEX.success}
        label={t("standlarim.borcDurumu", { v: hasDebt ? t("standlarim.var") : t("standlarim.yok") })}
        onClick={() => navigate(`/Firma/Standlar/${s.id}?tab=commitments`)}
      />
      <VDivider />
      {TEKNIK_TALEP_KATEGORILERI.map((cat) => {
        const req = teknikByKey[cat.key];
        const hasRequest = !!req?.hasRequest;
        return (
          <StatusTile
            key={cat.key}
            icon={TEKNIK_ICONS[cat.key]}
            color={hasRequest ? cat.color : NEUTRAL_GRAY}
            opacity={hasRequest ? 1 : 0.55}
            label={hasRequest ? cat.label : t("standlarim.talepEdilmedi", { label: cat.label })}
            onClick={() => navigate(`/Firma/Talepler/${s.id}?tab=teknikTalepler&kategori=${cat.key}`)}
          />
        );
      })}
    </div>
  );
}

const PAGE_SIZE = 10;

export default function FirmaStandListPage() {
  const { t: tx } = useTranslation("portal");
  const navigate = useNavigate();
  const { selectedFuarId } = useContext(MainContext);
  const portal = usePortal();
  const isLiveTestFuar = isLiveTestFuarId(selectedFuarId);
  // WAS (until #403): const firmaId = isLiveTestFuar ? TEST_DECORATOR_FIRMA_ID : getCurrentFirmaId();
  // null while /portal/context loads: the fetch effects below wait for it instead of asking for firma "null".
  const firmaId = isLiveTestFuar ? (portal?.firmaId ?? null) : getCurrentFirmaId();

  const [stands, setStands] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [reloadToken, setReloadToken] = useState(0);
  // standId(mongo .id) → Teknik Talepler kayıtları — standTalepService'te toplu
  // döndüren bir uç yok (bkz. firmaTalepListService.js'teki aynı çözüm,
  // buildStandSummary), bu yüzden stands yüklendikten sonra ayrı bir Promise.all
  // effect'iyle çekilir.
  const [teknikByStandId, setTeknikByStandId] = useState({});
  // Stand rozetinin arka plan rengi için — standDurumuService.getStandDurumuOzet
  // her stand için evrak durumunu (dekoratör firması bazlı, stand bazlı değil)
  // gerektirir, bu yüzden önce firmaId için bir kez çekilir (bkz. FirmaAnasayfaListPage.jsx'teki
  // aynı desen), sonra stands yüklendikten sonra ayrı bir effect'te her stand için özet hesaplanır.
  const [firmaEvrakData, setFirmaEvrakData] = useState({ profile: null, documents: [], firmaBelgeleri: {} });
  const [standDurumuByStandId, setStandDurumuByStandId] = useState({});

  useEffect(() => { document.title = i18n.t("portal:standlarim.standlarimFds"); }, []);

  // KURAL: fetch mantığı bilinçli olarak Effect içinde tanımlanır (üst seviye
  // useCallback olarak paylaşılmaz) — aksi halde react-hooks/set-state-in-effect
  // ihlali oluşur. Yenile butonu reloadToken'ı artırarak tetiklenir.
  useEffect(() => {
    // WAS (until #403): if (!selectedFuarId) return;
    if (!selectedFuarId || !firmaId) return;
    let ignore = false;

    async function run() {
      setLoading(true);
      setError(null);
      try {
        const data = await getStandOverviewList({
          fuarId: selectedFuarId,
          // WAS (until #425): decoratorFirmaId: firmaId,
          // Canlı: firmanın o fuarda HER rolde (Katılımcı/Dekoratör/Taşeron) olduğu standlar (backend #423);
          // decoratorFirmaId yalnızca Dekoratör satırlarını getirir, Katılımcı girişi boş liste görüyordu.
          // Mock dal decoratorFirmaId ile süzmeye devam eder.
          ...(isLiveTestFuar ? { firmaId } : { decoratorFirmaId: firmaId }),
          search,
          page: currentPage,
          limit: PAGE_SIZE,
          forceLive: isLiveTestFuar,
        });
        const records = data.records || [];
        if (!ignore) {
          // İLK BOYAMA roster'la — bilgi kutuları ve İnşa İzni artık burada.
          setStands(records);
          setTotal(data.total || 0);
          setLoading(false);
        }
        // İKİNCİ, ENGELLEMEYEN AŞAMA — köprüden geriye sadece Proje Beyanı kapısı
        // (projeBeyani.declared) ve belge sayaçları kaldı. Satır başına bir çağrı,
        // liste render'ını bloke etmez.
        if (isLiveTestFuar && records.length > 0) {
          const bridged = await Promise.all(
            records.map((s) => getProjectByStandId(s.standId, { forceLive: true }).catch(() => null))
          );
          if (!ignore) setStands(records.map((s, i) => mergeLiveProjectIntoStand(s, bridged[i])));
        }
      } catch (e) {
        if (!ignore) setError(e.message);
      } finally {
        if (!ignore) setLoading(false);
      }
    }

    run();
    return () => { ignore = true; };
  }, [selectedFuarId, firmaId, isLiveTestFuar, search, currentPage, reloadToken]);

  useEffect(() => {
    let ignore = false;

    Promise.all(stands.map((s) => getTeknikTalepler(s.id, { forceLive: isLiveTestFuar }).then((data) => [s.id, data])))
      .then((pairs) => {
        if (!ignore) setTeknikByStandId(Object.fromEntries(pairs));
      })
      .catch(() => {
        if (!ignore) setTeknikByStandId({});
      });

    return () => { ignore = true; };
  }, [stands, isLiveTestFuar]);

  useEffect(() => {
    if (!firmaId) return undefined; // #403: no request before the portal has delivered the company
    let ignore = false;

    Promise.all([
      getFirmaProfile(firmaId, { forceLive: isLiveTestFuar, fuarId: selectedFuarId }),
      getFirmaDocuments(firmaId, { forceLive: isLiveTestFuar, fuarId: selectedFuarId }),
      getFirmaBelgeleri(firmaId, { forceLive: isLiveTestFuar, fuarId: selectedFuarId }),
    ])
      .then(([profile, documents, firmaBelgeleri]) => {
        if (!ignore) setFirmaEvrakData({ profile, documents, firmaBelgeleri });
      })
      .catch(() => {
        if (!ignore) setFirmaEvrakData({ profile: null, documents: [], firmaBelgeleri: {} });
      });

    return () => { ignore = true; };
  }, [firmaId, isLiveTestFuar, selectedFuarId]);

  useEffect(() => {
    let ignore = false;

    Promise.all(stands.map((s) => getStandDurumuOzet(s, firmaEvrakData, { forceLive: isLiveTestFuar }).then((ozet) => [s.id, ozet])))
      .then((pairs) => {
        if (!ignore) setStandDurumuByStandId(Object.fromEntries(pairs));
      })
      .catch(() => {
        if (!ignore) setStandDurumuByStandId({});
      });

    return () => { ignore = true; };
  }, [stands, firmaEvrakData, isLiveTestFuar]);

  function handleSearchChange(e) {
    setSearch(e.target.value);
    setCurrentPage(1);
  }

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title={tx("standlarim.standlarim")} pageTitle={tx("standlarim.firma")} />

        {error && <Alert color="danger">{error}</Alert>}
        {!selectedFuarId && <Alert color="info">{tx("standlarim.buSayfayiGormekIcinOnce")}</Alert>}

        {selectedFuarId && (
          <Card className="p-3 mb-3" data-tour="standlar-arama">
            <Row className="align-items-center">
              <Col md="8">
                <Input
                  type="text"
                  placeholder={tx("standlarim.standSalonVeyaFirmaAra")}
                  value={search}
                  onChange={handleSearchChange}
                  disabled={loading}
                />
              </Col>
              <Col md="4" className="text-end">
                <Button
                  color="secondary" outline size="sm"
                  onClick={() => setReloadToken((t) => t + 1)}
                  disabled={loading}
                >
                  <RefreshCw size={14} className="me-1" />{" "}{tx("common:yenile")}
                </Button>
              </Col>
            </Row>
          </Card>
        )}

        {loading && (
          <div className="text-center py-5">
            <Spinner color="primary" />
            <p className="mt-2">{tx("common:yukleniyor")}</p>
          </div>
        )}

        {!loading && selectedFuarId && (
          stands.length > 0 ? (
            <>
              {stands.map((s) => (
                <Card key={s.id} className="mb-3" data-tour="stand-karti">
                  <CardBody>
                    <Row className="align-items-center g-3">
                      <Col md="auto">
                        <StandKodKutusu
                          stand={s}
                          color={SEVIYE_RENK[standDurumuByStandId[s.id]?.seviye] ?? SEVIYE_RENK.projeYok}
                          onClick={() => navigate(`/Firma/Standlar/${s.id}`)}
                          hint={tx("standlarim.detaylar")}
                          badgeTitle={tx("standlarim.dekorasyonTipi")}
                        />
                      </Col>

                      {/* Tabela Adı/Katılımcı Firma Adı/Dekoratör Adı, rozetin YANINDA —
                          bkz. bu Col'un dosya başı yorumu: tabelaAdi standCode'dan
                          (rozetin kendisi) ayrı bir alan. */}
                      <Col md={2}>
                        <div className="fw-bold fs-5">
                          {/* WAS (#208 öncesi): Tabela adı girilmedi */}
                          {s.tabelaAdi || <span className="text-muted fw-normal fs-6">{tx("standlarim.standAdiGirilmedi")}</span>}
                        </div>
                        <div className="text-muted small">{s.firmaName || "—"}</div>
                        <div className="text-muted small">{s.dekoratorFirmaName || "—"}</div>
                        {/* #425: bu firmanın bu standdaki rolü (Katılımcı / Dekoratör / Taşeron) */}
                        <StandRolRozeti record={s} className="d-block mt-1" />
                      </Col>

                      {/* Sabit piksel genişliği — pill metni ne kadar uzun/kısa olursa olsun
                          kutucuklar sonraki sütunda hep aynı x konumunda başlar. #488: 200 → 216
                          (Col'un 16 px dolgusu düşünce içerik 200 px): yedi pil'in tipik hâli
                          ("Özel Stand · Tek Katlı · Sunta - Boya · 24 m² · 4 m · 12 kW") ~190 px
                          ister, 184'te "Sunta - Boya" kesiliyordu; daha uzun adlar "…" ile kesilir. */}
                      <Col md="auto" style={{ width: 216 }}>
                        <StandInfoGrid stand={s} truncate />
                      </Col>

                      <Col className="d-flex align-items-center border-start ps-3">
                        <StandStatusTiles s={s} teknik={teknikByStandId[s.id]} navigate={navigate} />
                      </Col>

                      <Col md="auto" className="ms-md-auto mt-2 mt-md-0">
                        <Button
                          color="info" outline size="sm" title={tx("common:goruntule")}
                          onClick={() => navigate(`/Firma/Standlar/${s.id}`)}
                        >
                          <Eye size={14} className="me-1" />{tx("standlarim.detaylar")}
                        </Button>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              ))}

              {totalPages > 1 && (
                <div className="d-flex justify-content-center p-3">
                  <Pagination>
                    <PaginationItem disabled={currentPage === 1}>
                      <PaginationLink onClick={() => setCurrentPage(1)}>{tx("standlarim.ilk")}</PaginationLink>
                    </PaginationItem>
                    {Array.from({ length: totalPages }, (_, i) => (
                      <PaginationItem key={i} active={i + 1 === currentPage}>
                        <PaginationLink onClick={() => setCurrentPage(i + 1)}>{i + 1}</PaginationLink>
                      </PaginationItem>
                    ))}
                    <PaginationItem disabled={currentPage >= totalPages}>
                      <PaginationLink onClick={() => setCurrentPage(totalPages)}>{tx("standlarim.son")}</PaginationLink>
                    </PaginationItem>
                  </Pagination>
                </div>
              )}

              <div className="text-center text-muted small p-3">
                <p>{tx("standlarim.sayfaToplamStand", { currentPage, totalPages, total })}</p>
              </div>
            </>
          ) : (
            <Card>
              <div className="text-center p-5">
                <p className="text-muted">{tx("standlarim.buFuardaSizeBagliMusteri")}</p>
              </div>
            </Card>
          )
        )}
      </Container>
    </div>
  );
}
